import { Component, inject, type OnDestroy } from '@angular/core';
import { Company, CompanyService } from '@xilo-mono/form-contracts';
import { catchError, defaultIfEmpty, forkJoin, of, ReplaySubject, take, takeUntil, tap } from 'rxjs';
import { applyTheme, FormThemeService } from './common/form-theme.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styles: [
    `
        #google_translate_element {
            position: absolute;
            top: 1rem;
            right: 1rem;
        }
    `
  ],
  providers: [FormThemeService],
})
export class AppComponent implements OnDestroy {
  protected hasGoogleTranslate = false;
  private readonly destroyed$ = new ReplaySubject<boolean>(1);
  private readonly formThemeService = inject(FormThemeService);
  private readonly companyService = inject(CompanyService);

  constructor() {
    const companyId = getQueryVariable('companyId');
    const themeId = getQueryVariable('themeId');
    this.hasGoogleTranslate = !!getQueryVariable('google_translate');
    this.initFormThemeLoading(themeId, companyId);

    if (companyId) {
      forkJoin([
        this.companyService.getCompanyDetailsById(companyId).pipe(
          take(1),
          defaultIfEmpty(undefined),
          catchError(() => of(undefined))
        ),
        this.formThemeService.formTheme$.pipe(take(1)),
      ]).pipe(
        tap(([res, formTheme]) => {
          if (res?.obj) {
            this.setFontTheme(res.obj);
          }
          if (formTheme?.data) {
            applyTheme(formTheme.data);
          }
        }),
        takeUntil(this.destroyed$)
      ).subscribe();
    }

    if (this.hasGoogleTranslate) {
      this.loadGoogleTranslate();
    }
  }

  ngOnDestroy() {
    this.destroyed$.next(true);
  }

  loadGoogleTranslate() {
    const src = '//translate.google.com/translate_a/element.js?cb=googleTranslateElementInit';

    // Check if the script is already included
    if (Array.from(document.scripts).some(s => s.src.includes('translate.google.com/translate_a/element.js'))) {
      console.log('Google Translate script already loaded.');
      return;
    }

    // Proceed to load the script
    const script: HTMLScriptElement = document.createElement('script');
    script.type = 'text/javascript';
    script.async = true;
    script.src = src;
    document.head.appendChild(script);
  }

  public setFontTheme(company: Company) {
    const theme = {
      'heading-font-color': company.form_font_settings?.heading.color,
      'heading-font-weight': company.form_font_settings?.heading.weight,
      'heading-font-family': company.form_font_settings?.heading.family,
      'heading-font-size': company.form_font_settings?.heading.size,
      'title-font-color': company.form_font_settings?.title.color,
      'title-font-weight': company.form_font_settings?.title.weight,
      'title-font-family': company.form_font_settings?.title.family,
      'title-font-size': company.form_font_settings?.title.size,
      'body-font-color': company.form_font_settings?.body.color,
      'body-font-weight': company.form_font_settings?.body.weight,
      'body-font-family': company.form_font_settings?.body.family,
      'body-font-size': company.form_font_settings?.body.size,
      'sub-heading-font-color': company.form_font_settings?.subHeading.color,
      'sub-heading-font-weight': company.form_font_settings?.subHeading.weight,
      'sub-heading-font-family': company.form_font_settings?.subHeading.family,
      'sub-heading-font-size': company.form_font_settings?.subHeading.size,
      'label-font-color': company.form_font_settings?.label.color,
      'label-font-weight': company.form_font_settings?.label.weight,
      'label-font-family': company.form_font_settings?.label.family,
      'label-font-size': company.form_font_settings?.label.size,
      'form-logo-size': company.logoHeight,
    };
    Object.keys(theme).forEach((k) => {
      if (theme[k]) {
        document.documentElement.style.setProperty(`--${k}`, theme[k]);
      }
    });
  }

  private initFormThemeLoading(themeId: string | undefined, companyId: string | undefined) {
    try {
      this.formThemeService.loadFormTheme(themeId, companyId).pipe(
        takeUntil(this.destroyed$)
      ).subscribe();
    } catch (e) {
      console?.error('setThemeByUrlParam:', e);
    }
  }

}

function getQueryVariable(variable: string): string | undefined {
  try {
    const query = window.location.search.substring(1);
    const vars = query.split('&');
    for (let i = 0; i < vars.length; i++) {
      const pair = vars[i].split('=');
      if (decodeURIComponent(pair[0]) === variable) {
        return decodeURIComponent(pair[1]);
      }
    }
  } catch (e) {
    console?.error('getQueryVariable:', e);
  }
  return undefined;
}